/* eslint-disable max-classes-per-file */
// import Api, { ApiResponse, BadApiResult } from "../../api";
// import { AccelFile } from "../../models";
// import TypedEvent from "../typed-event";

import { AccelFile, FileApi, FileSource, PartETag } from "@axl/membership-sdk";

import TypedEvent from "./typed-event";

// import type { PartETag, UploadFileArgs } from "../../api/methods/file";

export class S3UploaderOptions {
  public chunkSize: number = 50 * 1024 * 1024;

  public resume: boolean = true;

  public maxAttempts: number = 3;

  public concurrentUploads: number = 10;
}

export type BeginEventArgs = {
  id: string;
};
export type ProgressEventArgs = {
  id: string;
  percent: number;
  total: number;
  chunk: number;
};
export type CompleteEventArgs = {
  file: AccelFile;
};
export type ErrorEventArgs = {
  errors?: unknown;
};
export type AbortEventArgs = object;

type FileMeta = {
  id: string;
};

export type UploadFileArgs = {
  source?: FileSource;
  fileId?: string;
  courseId?: string;
  schoolId?: string;
  courseItemId?: string;
  courseItemTaskId?: string;
  courseItemTestId?: string;
  courseItemTestQuestionId?: string;
  courseItemTestAnswerId?: string;
  courseItemTaskAnswerId?: string;
  courseItemTaskAnswerCommentId?: string;
  isPublic?: boolean;
  siteId?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  meta?: any;
  voice?: boolean;
};

export default class S3Uploader {
  public onBegin = new TypedEvent<BeginEventArgs>();

  public onProgress = new TypedEvent<ProgressEventArgs>();

  public onComplete = new TypedEvent<CompleteEventArgs>();

  public onPrepare = new TypedEvent<void>();

  public onError = new TypedEvent<ErrorEventArgs>();

  public onAborted = new TypedEvent<AbortEventArgs>();

  public isAborted: boolean = false;

  public isUploading: boolean = false;

  private options: S3UploaderOptions;

  private file: File;

  private args: UploadFileArgs;

  private fileMeta: FileMeta | null;

  constructor(
    private api: FileApi,
    options: Partial<S3UploaderOptions> = new S3UploaderOptions(),
  ) {
    this.options = { ...new S3UploaderOptions(), ...options };
  }

  setOptions(options: Partial<S3UploaderOptions>) {
    this.options = { ...new S3UploaderOptions(), ...options };
  }

  setFile(file: File, args: UploadFileArgs = {}): boolean {
    if (this.isUploading) return false;
    this.fileMeta = null;
    this.file = file;
    this.args = args;

    return true;
  }

  async upload(): Promise<boolean> {
    if (!this.file) throw new Error("File is not specified");
    if (this.isUploading) throw new Error("Upload is in progress");

    this.isUploading = true;
    const urls = await this.beginUpload();
    if (urls.length === 0) return false;

    const eTags = await this.uploadParts(urls);
    if (eTags.length === 0) return false;

    await this.complete(eTags);

    return this.prepare();
  }

  private async beginUpload(): Promise<string[]> {
    const result = await this.api.begin(
      this.file,
      this.options.chunkSize,
      this.args,
    );
    if (!result.success) {
      this.onError.emit({
        errors: result.errors,
      });

      return [];
    }

    this.onBegin.emit({
      id: result.body.id,
    });
    this.fileMeta = {
      id: result.body.id,
    };

    return result.body.urls;
  }

  public async abort(): Promise<boolean> {
    if (this.isAborted) return Promise.resolve(true);

    this.isAborted = true;
    // in case 'begin' request is in progress
    if (this.fileMeta == null) return true;

    this.isUploading = false;
    const result = await this.api.abort(this.fileMeta!.id);
    if (!result.success) {
      this.onError.emit({
        errors: result.errors,
      });

      return false;
    }

    this.onAborted.emit({});

    return true;
  }

  private async uploadParts(urls: string[]): Promise<PartETag[]> {
    if (this.isAborted) return [];

    let fulfilled = 0;
    let concurentUploads: Promise<PartETag>[] = [];
    let tags: PartETag[] = [];

    for (let i = 0; i < urls.length; i++) {
      const url = urls[i];
      const partNumber = i + 1;
      const start = i * this.options.chunkSize;
      const end = partNumber * this.options.chunkSize;
      const blob = this.file.slice(start, end);
      const isLastChunk = i === urls.length - 1;

      if (this.isAborted) break;

      const promise = this.uploadPart(url!, blob, partNumber);
      concurentUploads.push(promise);

      promise.then(
        // eslint-disable-next-line no-loop-func
        () => {
          // eslint-disable-next-line no-plusplus
          fulfilled++;
          this.onProgress.emit({
            chunk: partNumber,
            id: this.fileMeta!.id,
            total: urls.length,
            percent: Math.round((fulfilled / urls.length) * 100),
          });
        },
        (reason) =>
          this.onError.emit({
            errors: [{ message: reason, number: partNumber }],
          }),
      );

      if (
        (this.options.concurrentUploads > 0 &&
          concurentUploads.length >= this.options.concurrentUploads) ||
        isLastChunk
      ) {
        // eslint-disable-next-line no-await-in-loop
        tags = [...tags, ...(await Promise.all(concurentUploads))];
        concurentUploads = [];
      }
    }

    return tags;
  }

  private async uploadPart(
    url: string,
    blob: Blob,
    partNumber: number,
  ): Promise<PartETag> {
    let attempts = 0;
    do {
      // eslint-disable-next-line no-plusplus
      attempts++;
      try {
        // eslint-disable-next-line no-await-in-loop
        const response = await fetch(url, {
          method: "PUT",
          body: blob,
        });
        if (response != null && response.ok) {
          const eTag: string = response.headers.get("etag") as string;

          return Promise.resolve({ eTag, partNumber });
        }
      } catch (e: unknown) {
        // let result: BadApiResult;
        // if (e.response == null || e.response.data == null) {
        //   result = new BadApiResult();
        //   console.error("Network error", e);
        // } else {
        //   const response = new ApiResponse(e.response.data);
        //   result = new BadApiResult(e.response.status, response);
        // }
        // this.onError.emit({
        //   errors: result.hasError ? [result.error] : result.response.errors,
        // });
      }
    } while (attempts !== this.options.maxAttempts && !this.isAborted);

    // auto abort whole file
    await this.abort();

    // eslint-disable-next-line prefer-promise-reject-errors
    return Promise.reject("Maximum retries reached");
  }

  private async complete(eTags: PartETag[]): Promise<boolean> {
    if (this.isAborted) return false;
    this.isUploading = false;
    const result = await this.api.complete(this.fileMeta!.id, eTags);
    if (!result.success) {
      this.onError.emit({
        errors: result.errors,
      });

      return false;
    }

    this.onComplete.emit({
      file: result.body,
    });

    return true;
  }

  private async prepare(): Promise<boolean> {
    if (this.isAborted) return false;
    const result = await this.api.prepare(this.fileMeta!.id);
    if (!result.success) {
      this.onError.emit({
        errors: result.errors,
      });

      return false;
    }

    this.onPrepare.emit();

    return true;
  }
}
